// PreviewCanvas extracted from OfferPageBuilderSimple.js lines 13200-13611
// Contains the Canvas+Right Panel Container: Live Preview column and Editable Canvas
import React from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { renderPreviewElement } from '../../../utils/offerPreviewElement';
import {
  BRANDS_EDITOR_PREVIEW_COL_WIDTH,
  SETUPS_EDITOR_PREVIEW_COL_WIDTH,
  BRANDS_EDITOR_DEVICE_WIDTH,
  BRANDS_EDITOR_DEVICE_HEIGHT,
  SETUPS_EDITOR_DEVICE_WIDTH,
  SETUPS_EDITOR_DEVICE_HEIGHT,
  BRANDS_EDITOR_LABEL,
  SETUPS_EDITOR_LABEL,
  BRANDS_PAGE_MAX_HEIGHT,
} from '../../../utils/offerDeviceConstants';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';
import { useOfferBuilder } from '../OfferBuilderContext';
import PaginationControls from './PaginationControls';

const PreviewCanvas = () => {
  const {
    isMobile,
    isBrandsPage,
    templatesOnly,
    customElementsOnly,
    drawerOpen,
    previewDevice,
    previewPanelOpen,
    pageSettings,
    pages,
    elements,
    currentPageIndex,
    paginationEnabled,
    isEditingMode,
    selectedElement,
    selectedElementContext,
    selectedSubElement,
    tabValue,
    rightPanelOpen,
    forceRender,
    getPreviewStyles,
    getCanvasStyles,
    getZoneHeightStyle,
    getCanvasOverflowAlert,
    renderInlineEditableElement,
    addElement,
    updateElement,
    moveToNewPage,
    setTabValue,
    setRightPanelOpen,
    getBrandsPageOverflowInfo,
  } = useOfferBuilder();

  return (
    <>
            {/* Live Preview Panel - Left Side */}
            {!isMobile && previewPanelOpen && (
              <Box sx={{
                width: !templatesOnly
                  ? (isBrandsPage ? BRANDS_EDITOR_PREVIEW_COL_WIDTH : (drawerOpen ? 330 : 340))
                  : (isBrandsPage ? BRANDS_EDITOR_PREVIEW_COL_WIDTH : 340),
                flexShrink: 0,
                minWidth: 0,
                borderRight: '1px solid #e0e0e0',
                overflow: 'hidden',
                backgroundColor: '#f8f9fa',
                p: 0.5
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  pb: 0.5,
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    Live Preview
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({isBrandsPage ? BRANDS_EDITOR_LABEL : SETUPS_EDITOR_LABEL} display)
                  </Typography>

                </Box>

                <Paper
                  elevation={2}
                  sx={{
                    backgroundColor: pageSettings.backgroundColor,
                    ...getPreviewStyles(),
                    width: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_WIDTH}px` : `${SETUPS_EDITOR_DEVICE_WIDTH}px`) : 'auto',
                    maxWidth: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_WIDTH}px` : `${SETUPS_EDITOR_DEVICE_WIDTH}px`) : 'none',
                    height: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : `${SETUPS_EDITOR_DEVICE_HEIGHT}px`) : 'auto',
                    minHeight: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : `${SETUPS_EDITOR_DEVICE_HEIGHT}px`) : '400px',
                    position: 'relative',
                    overflow: 'hidden', // Match device bounds
                    margin: '0 auto',
                    // Inner content area styling
                    ...(previewDevice === 'mobile' && {
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: isBrandsPage ? 'center' : 'flex-start',
                      alignItems: isBrandsPage ? 'center' : 'flex-start',
                      p: 0, // Remove default padding when in mobile mode
                    }),
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      background: 'linear-gradient(45deg, transparent 49%, rgba(0,0,0,0.02) 50%, transparent 51%)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }
                  }}
                >
                  <Box sx={{
                    // Match device inner content area dimensions exactly
                    ...(previewDevice === 'mobile' && {
                      width: isBrandsPage ? '100%' : '375px',
                      height: isBrandsPage ? '100%' : '667px',
                      minHeight: isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : '667px',
                      maxHeight: isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : '667px',
                      transform: isBrandsPage ? 'none' : 'scale(0.827)',
                      transformOrigin: 'top left',
                      margin: 0,
                      backgroundColor: '#fff',
                      borderRadius: 0,
                      overflow: 'hidden',
                      padding: '20px',
                      paddingBottom: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box'
                    }),
                    ...(previewDevice !== 'mobile' && {
                      maxWidth: pageSettings.maxWidth === '100%' ? '100%' : pageSettings.maxWidth,
                      margin: '0 auto'
                    }),
                    position: 'relative',
                    zIndex: 2,
                    pointerEvents: 'none' // Prevent interaction with preview elements
                  }}>
                    {elements.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          📱 Live Preview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add elements to see live preview
                        </Typography>
                      </Box>
                    ) : (
                      elements.map((element, index) => (
                        <Box key={element.id} sx={{ mb: index === elements.length - 1 ? 0 : 1 }}>
                          {element.type === ELEMENT_TYPES.LAYOUT ? (
                            <Box sx={{
                              backgroundColor: element.styles?.backgroundColor || 'transparent',
                              margin: element.styles?.margin || '0 0 16px 0',
                              ...element.styles,
                              p: 0, // Force zero padding - override any saved padding
                              border: 'none', // Force no border in preview - override any saved border
                              borderRadius: 0 // Force no border radius in preview
                            }}>
                              <Box sx={{
                                display: 'flex',
                                gap: 0,
                                flexDirection: { xs: 'column', md: 'row' }
                              }}>
                                {element.zones.map((zone, zoneIndex) => (
                                  <Box key={zone.id} sx={{
                                    flex: 1,
                                    border: 'none', // No border in preview
                                    borderRadius: 0,
                                    p: 0,
                                    minHeight: '100px',
                                    position: 'relative'
                                  }}>
                                    {zone.elements.map((zoneElement) => (
                                      <Box key={`${zoneElement.id}-${forceRender}`} sx={{ mb: 1 }}>
                                        {renderPreviewElement(zoneElement, getZoneHeightStyle, forceRender)}
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            renderPreviewElement(element, getZoneHeightStyle, forceRender)
                          )}
                        </Box>
                      ))
                    )}
                  </Box>

                  {/* Fixed Bottom Bar - Same as blog */}
                  {previewDevice === 'mobile' && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '60px',
                      backgroundColor: '#000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      zIndex: 10000,
                      width: '100%',
                      px: 2,
                      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                    }}>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Editable Canvas - Right Side */}
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: 'hidden',
              p: { xs: 0.5, md: 0.5 }, // Reduced padding to give more space
              pb: { xs: 10, md: 0.5 }, // Reduced bottom padding
              backgroundColor: previewDevice === 'mobile' ? '#f0f0f0' : 'transparent'
            }}>
              {/* Canvas Header with Pagination Controls */}
              {!isMobile && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  pb: 0.5,
                  borderBottom: '1px solid #e0e0e0',
                  minWidth: 0 // Allow overflow handling
                }}>
                  <Typography variant="subtitle2" color="primary" noWrap sx={{ fontWeight: 700, fontSize: '0.8rem', flexShrink: 1, minWidth: 0 }}>
                    Editable Canvas
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 1, flexShrink: 1, minWidth: 0 }}>
                    ({isBrandsPage ? BRANDS_EDITOR_LABEL : SETUPS_EDITOR_LABEL} display)
                  </Typography>

                  {/* Pagination Controls in Header */}
                  <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                    <PaginationControls />
                  </Box>
                </Box>
              )}

              {/* Editable Canvas with Device Frame */}
              <Paper
                elevation={2}
                sx={{
                  backgroundColor: pageSettings.backgroundColor,
                  ...getPreviewStyles(),
                  width: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_WIDTH}px` : `${SETUPS_EDITOR_DEVICE_WIDTH}px`) : 'auto',
                  maxWidth: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_WIDTH}px` : `${SETUPS_EDITOR_DEVICE_WIDTH}px`) : 'none',
                  height: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : `${SETUPS_EDITOR_DEVICE_HEIGHT}px`) : 'auto',
                  minHeight: previewDevice === 'mobile' ? (isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : `${SETUPS_EDITOR_DEVICE_HEIGHT}px`) : '400px',
                  position: 'relative',
                  overflow: 'hidden', // Match device bounds
                  margin: '0 auto',
                  // Inner content area styling
                  ...(previewDevice === 'mobile' && {
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isBrandsPage ? 'center' : 'flex-start',
                    alignItems: isBrandsPage ? 'center' : 'flex-start',
                    p: 0, // Remove default padding when in mobile mode
                  }),
                }}>
                {/* Static canvas container (no Droppable) */}
                <Box
                  sx={{
                    // Use only canvas styles, not preview styles to avoid conflicts
                    ...getCanvasStyles(),
                    // Override with editing-specific styles
                    border: '2px dashed #e0e0e0',
                    overflow: 'hidden', // No scrollbar
                    position: 'relative', // For absolute positioning of undo button
                    backgroundColor: '#fff', // Match preview background
                    // Match blog dimensions and preview styling exactly
                    ...(previewDevice === 'mobile' && {
                      width: isBrandsPage ? '100%' : '375px',
                      height: isBrandsPage ? '100%' : '667px',
                      minHeight: isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : '667px',
                      maxHeight: isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : '667px',
                      transform: isBrandsPage ? 'none' : 'scale(0.827)',
                      transformOrigin: 'top left',
                      padding: '20px',
                      paddingBottom: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                      // Note: Last element margin is handled by wrapper Box, not here
                    }),
                    // Fallback for non-mobile modes
                    ...(previewDevice !== 'mobile' && {
                      minHeight: '400px',
                      borderRadius: 2,
                      p: 0.5
                    })
                  }}
                >

                  {/* Canvas Overflow Alert */}
                  {getCanvasOverflowAlert() && (
                    <Alert
                      severity="warning"
                      sx={{
                        mb: 2,
                        fontSize: '0.75rem',
                        py: 0.5,
                        '& .MuiAlert-message': {
                          fontSize: '0.75rem'
                        }
                      }}
                    >
                      {getCanvasOverflowAlert()}
                    </Alert>
                  )}

                  {/* Brands Page Height Limit Red Line */}
                  {isBrandsPage && getBrandsPageOverflowInfo() && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: `${BRANDS_PAGE_MAX_HEIGHT}px`,
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#e53e3e',
                        zIndex: 1000,
                        boxShadow: '0 0 4px rgba(229, 62, 62, 0.5)',
                        '&::before': {
                          content: '"Height Limit"',
                          position: 'absolute',
                          top: '-20px',
                          left: '8px',
                          fontSize: '10px',
                          color: '#e53e3e',
                          fontWeight: 'bold',
                          backgroundColor: 'white',
                          padding: '2px 4px',
                          borderRadius: '2px',
                          border: '1px solid #e53e3e'
                        }
                      }}
                    />
                  )}

                  {/* Brands Page Overflow Alert */}
                  {isBrandsPage && getBrandsPageOverflowInfo() && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        fontSize: '0.75rem',
                        py: 0.5,
                        '& .MuiAlert-message': {
                          fontSize: '0.75rem'
                        }
                      }}
                    >
                      ⚠️ Content exceeds brands page height limit by {getBrandsPageOverflowInfo().overflowHeight}px (Current: {getBrandsPageOverflowInfo().totalHeight}px, Max: {getBrandsPageOverflowInfo().maxHeight}px)
                    </Alert>
                  )}




                  {elements.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        📱 Editable Canvas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isMobile
                          ? 'Tap the menu button to add elements'
                          : !templatesOnly
                            ? (drawerOpen
                              ? 'Drag elements here or click to add'
                              : 'Click "Add Elements" in the toolbar to get started')
                            : 'Edit content directly or load templates for more options'
                        }
                      </Typography>
                      {isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          💡 Live preview available on desktop
                        </Typography>
                      )}
                      {!isMobile && !previewPanelOpen && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          💡 Click "Show Preview" in the toolbar to see live changes
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    elements.map((element, index) => {
                      console.log('🎨 Rendering canvas element:', element.id, 'type:', element.type, 'forceRender:', forceRender);
                      if (element.type === ELEMENT_TYPES.VIDEO) {
                        console.log('🎥 Canvas VIDEO element details:', {
                          id: element.id,
                          videoFile: element.videoFile,
                          content: element.content
                        });
                      }
                      const isVideo = element.type === ELEMENT_TYPES.VIDEO;
                      return (
                        <Box
                          key={element.id}
                          sx={{
                            mt: index === 0 ? 0 : undefined,
                            mb: index === elements.length - 1 ? 0 : (element.type === ELEMENT_TYPES.HEADING ? 0.25 : 1), // Tight 2px gap after headings, 8px elsewhere
                            display: 'block', // Ensure margins work properly
                            // Override element's margin-bottom to match preview spacing
                            '& > *': {
                              marginBottom: '0 !important' // Remove element's own margin-bottom, use wrapper spacing
                            },
                            // Ensure video elements maintain their margin-bottom spacing (4px = mb: 0.5)
                            ...(isVideo && {
                              '& [data-element-id]': {
                                mb: '4px !important' // Force 4px margin-bottom for videos
                              }
                            })
                          }}
                        >
                          {renderInlineEditableElement(element, false, null, null, index)}
                        </Box>
                      );
                    })
                  )}

                  {/* Fixed Bottom Bar - Same as blog */}
                  {previewDevice === 'mobile' && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '60px',
                      backgroundColor: '#000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      zIndex: 10000,
                      width: '100%',
                      px: 2,
                      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                    }}>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
    </>
  );
};

export default PreviewCanvas;
